import React, { useEffect, useMemo, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Icon } from "semantic-ui-react";
import { shallowEqual, useSelector } from "react-redux";
import QuestionDistributionChart from "../../app/audit/simple/question/QuestionDistributionChart";

const DISTRIBUTION_SEGMENTS = [
  { key: "Low", label: "Low", color: "#F15062" },
  { key: "Neutral", label: "Neutral", color: "#B3B3B3" },
  { key: "High", label: "High", color: "#1D9A7D" },
];

const SCORE_BANDS = {
  Low: { min: 0, max: 4 },
  Neutral: { min: 5, max: 6 },
  High: { min: 7, max: 10 },
};

const EPSILON = 0.0001;

const SEGMENT_META = DISTRIBUTION_SEGMENTS.reduce((accumulator, segment) => {
  accumulator[segment.key] = segment;
  return accumulator;
}, {});

const formatNumber = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  const rounded = Math.round(value * 100) / 100;
  if (Number.isInteger(rounded)) {
    return String(rounded);
  }
  return rounded.toFixed(2).replace(/\.?0+$/, "");
};

const formatRange = (start, end) => `${formatNumber(start)}–${formatNumber(end)}`;

const categorizeScore = (rawScore) => {
  const score = parseFloat(rawScore);
  if (!Number.isFinite(score)) return null;
  if (score <= SCORE_BANDS.Low.max + EPSILON) return "Low";
  if (score <= SCORE_BANDS.Neutral.max + EPSILON) return "Neutral";
  if (score <= SCORE_BANDS.High.max + EPSILON) return "High";
  if (score < SCORE_BANDS.Low.min) return "Low";
  if (score > SCORE_BANDS.High.max) return "High";
  return null;
};

const buildBandDistribution = (question) => {
  if (!question || question.type !== "quantitative") return null;
  const distribution = question.responses?.distribution || {};

  const counts = { Low: 0, Neutral: 0, High: 0 };
  let countedTotal = 0;

  Object.entries(distribution).forEach(([scoreKey, countValue]) => {
    const count = Number(countValue) || 0;
    if (count <= 0) return;
    const band = categorizeScore(scoreKey);
    if (!band) return;
    counts[band] += count;
    countedTotal += count;
  });

  if (!countedTotal) return null;

  const segments = DISTRIBUTION_SEGMENTS.reduce((accumulator, segment) => {
    const count = counts[segment.key] || 0;
    if (!count) return accumulator;
    const percentage = (count / countedTotal) * 100;
    accumulator.push({
      name: segment.label,
      key: segment.key,
      data: [Number(percentage.toFixed(1))],
      color: segment.color,
      count,
    });
    return accumulator;
  }, []);

  const ranges = {
    Low: { start: SCORE_BANDS.Low.min, end: SCORE_BANDS.Low.max },
    Neutral: { start: SCORE_BANDS.Neutral.min, end: SCORE_BANDS.Neutral.max },
    High: { start: SCORE_BANDS.High.min, end: SCORE_BANDS.High.max },
  };

  return { segments, ranges };
};

const buildSegmentsForScores = (scores) => {
  const counts = { Low: 0, Neutral: 0, High: 0 };

  (scores || []).forEach((value) => {
    const band = categorizeScore(value);
    if (!band) return;
    counts[band] += 1;
  });

  const total = counts.Low + counts.Neutral + counts.High;

  return DISTRIBUTION_SEGMENTS.reduce((accumulator, segment) => {
    const count = counts[segment.key] || 0;
    if (!count || !total) return accumulator;
    const percentage = (count / total) * 100;
    accumulator.push({
      name: segment.label,
      key: segment.key,
      data: [Number(percentage.toFixed(1))],
      color: segment.color,
      count,
    });
    return accumulator;
  }, []);
};

const buildOpenEndedResponses = (question, respondents, isAnonymous) => {
  if (!question || question.type !== "openended") return [];

  return (respondents || [])
    .map((respondent) => {
      const responseData = respondent.responses?.[question.id];
      const response =
        typeof responseData === "string"
          ? responseData
          : responseData?.response;
      const followUp =
        typeof responseData === "object" ? responseData?.followUp : null;

      if (!response || !response.toString().trim()) {
        return null;
      }

      return {
        name: isAnonymous ? "Anonymous Respondent" : respondent.name,
        response,
        followUp,
      };
    })
    .filter(Boolean);
};

const PulseAnalytics = ({
  pulseData = null,
  isAnonymous = false,
  orgRule: _orgRule = null,
  surveyInfo: _surveyInfo = {},
  onModalOpen,
}) => {
  const [activePrimaryTab, setActivePrimaryTab] = useState("scores");
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [selectedParentCategory, setSelectedParentCategory] = useState(null);
  const [selectedOpenEndedId, setSelectedOpenEndedId] = useState(null);

  const { get_employee_org_category } = useSelector(
    (state) => ({
      get_employee_org_category: state.employee_category.employee_org_category,
    }),
    shallowEqual
  );

  const { get_survey_structure, core_data } = useSelector(
    (state) => ({
      get_survey_structure: state.surveystructure?.survey_structure,
      core_data: state.audit?.core_data,
    }),
    shallowEqual
  );

  const getUserCategory = (employeeId) => {
    if (!get_employee_org_category || get_employee_org_category.length === 0) {
      return "Unknown Category";
    }

    const user = get_employee_org_category.find(
      (candidate) =>
        candidate.employee_id === employeeId ||
        candidate.employee === employeeId
    );

    const category = user?.category?.find(
      (cat) => cat?.priority === "secondary"
    )?.value;

    return category || "Unknown Category";
  };

  const dataSource = useMemo(() => {
    if (!pulseData) return pulseData;

    return {
      ...pulseData,
      respondents: pulseData.respondents?.map((respondent) => ({
        ...respondent,
        department: getUserCategory(respondent.id),
      })),
    };
  }, [pulseData, get_employee_org_category]);

  const hasRegisteredModalOpen = useRef(false);

  useEffect(() => {
    if (hasRegisteredModalOpen.current) return;
    if (!onModalOpen || typeof onModalOpen !== "function") return;

    onModalOpen(() => {});
    hasRegisteredModalOpen.current = true;
  }, [onModalOpen]);

  const quantitativeSummaries = useMemo(() => {
    return (dataSource?.questions || [])
      .filter((question) => question.type === "quantitative")
      .map((question) => {
        const bandData = buildBandDistribution(question);
        if (!bandData) return null;
        const { segments, ranges } = bandData;
        return {
          question,
          bands: segments,
          ranges,
          total: segments.reduce(
            (sum, item) => sum + (item.count || 0),
            0
          ),
        };
      })
      .filter(Boolean);
  }, [dataSource]);

  const openEndedQuestions = useMemo(
    () =>
      (dataSource?.questions || []).filter(
        (question) => question.type === "openended"
      ),
    [dataSource]
  );

  useEffect(() => {
    if (
      activePrimaryTab === "scores" &&
      quantitativeSummaries.length === 0 &&
      openEndedQuestions.length > 0
    ) {
      setActivePrimaryTab("openEnded");
    }
  }, [activePrimaryTab, quantitativeSummaries, openEndedQuestions]);

  useEffect(() => {
    if (!quantitativeSummaries.length) {
      if (selectedQuestionId !== null) {
        setSelectedQuestionId(null);
      }
      return;
    }

    if (!selectedQuestionId) return;

    const stillExists = quantitativeSummaries.some(
      (summary) => summary.question.id === selectedQuestionId
    );

    if (!stillExists) {
      setSelectedQuestionId(null);
    }
  }, [quantitativeSummaries, selectedQuestionId]);

  useEffect(() => {
    if (!openEndedQuestions.length) {
      setSelectedOpenEndedId(null);
      return;
    }

    if (!selectedOpenEndedId) {
      setSelectedOpenEndedId(openEndedQuestions[0].id);
      return;
    }

    const stillExists = openEndedQuestions.some(
      (question) => question.id === selectedOpenEndedId
    );

    if (!stillExists) {
      setSelectedOpenEndedId(openEndedQuestions[0].id);
    }
  }, [openEndedQuestions, selectedOpenEndedId]);

  const visibleCategories = useMemo(() => {
    const categories =
      core_data?.categories?.[0]?.categories ||
      get_survey_structure?.[0]?.categories;
    if (!categories) return [];
    return categories.filter((category) => category?.visibility !== "private");
  }, [core_data, get_survey_structure]);

  const parentCategoryOptions = useMemo(() => {
    return (visibleCategories || [])
      .filter((category) => category?.priority !== "primary")
      .map((category) => ({
        key: category.name,
        name: category.name,
        priority: category.priority,
      }));
  }, [visibleCategories]);

  useEffect(() => {
    const validKeys = new Set(
      (parentCategoryOptions || []).map((option) => option.key)
    );

    if (!validKeys.size) {
      setSelectedParentCategory(null);
      return;
    }

    if (!selectedParentCategory || !validKeys.has(selectedParentCategory)) {
      const preferred =
        parentCategoryOptions.find(
          (option) => option.priority === "secondary"
        ) || parentCategoryOptions[0];
      setSelectedParentCategory(preferred.key);
    }
  }, [parentCategoryOptions, selectedParentCategory]);

  const selectedQuestionSummary = useMemo(
    () =>
      quantitativeSummaries.find(
        (summary) => summary.question.id === selectedQuestionId
      ) || null,
    [quantitativeSummaries, selectedQuestionId]
  );

  const selectedQuestion = selectedQuestionSummary?.question || null;
  const sharedRangeInfo = useMemo(() => {
    if (!quantitativeSummaries.length) return null;
    const firstRanges = quantitativeSummaries[0].ranges;
    if (!firstRanges) return null;

    const consistent = quantitativeSummaries.every((summary) =>
      summary.ranges &&
      DISTRIBUTION_SEGMENTS.every(({ key }) => {
        const candidate = summary.ranges[key];
        const baseline = firstRanges[key];
        if (!candidate || !baseline) return false;
        return (
          Math.abs(candidate.start - baseline.start) < 0.0001 &&
          Math.abs(candidate.end - baseline.end) < 0.0001
        );
      })
    );

    return { ranges: firstRanges, consistent };
  }, [quantitativeSummaries]);

  const categoryDistributions = useMemo(() => {
    if (
      !selectedQuestion ||
      selectedQuestion.type !== "quantitative" ||
      !selectedParentCategory
    ) {
      return [];
    }

    const parent = (visibleCategories || []).find(
      (category) => category?.name === selectedParentCategory
    );
    if (!parent) return [];

    const children = [];
    (parent.options || []).forEach((option) => {
      if (option?.id) {
        children.push({ id: option.id, name: option.name });
      } else if (option?.name) {
        children.push({ name: option.name });
      }
      (option?.subfactors || []).forEach((subfactor) => {
        if (subfactor?.id) {
          children.push({ id: subfactor.id, name: subfactor.name });
        }
      });
    });

    if (!children.length) return [];

    const employeeMembership = new Map();
    (get_employee_org_category || []).forEach((member) => {
      const memberships = member.category || [];
      let match = null;
      if (parent.priority === "primary" || parent.priority === "secondary") {
        match = memberships.find(
          (category) => category?.priority === parent.priority
        );
      } else {
        match = memberships.find((category) => category?.name === parent.name);
      }
      if (!match) return;
      const identifier = match.value_id || match.value || match.name;
      if (!identifier) return;
      employeeMembership.set(
        member.employee_id || member.employee,
        identifier
      );
    });

    const scoresByChild = new Map();
    children.forEach((child) => {
      const key = child.id || child.name;
      if (key) {
        scoresByChild.set(key, []);
      }
    });

    (dataSource?.respondents || []).forEach((respondent) => {
      const responseRecord =
        respondent.responses?.[selectedQuestion.id]?.response ??
        respondent.responses?.[selectedQuestion.id];
      const score = parseFloat(responseRecord);
      if (!Number.isFinite(score)) return;

      const membershipKey = employeeMembership.get(respondent.id);
      if (!membershipKey || !scoresByChild.has(membershipKey)) return;

      scoresByChild.get(membershipKey).push(score);
    });

    return children
      .map((child) => {
        const key = child.id || child.name;
        if (!key) return null;
        const scores = scoresByChild.get(key) || [];
        const segments = buildSegmentsForScores(scores);
        const total = scores.length;
        const average =
          scores.length > 0
            ? scores.reduce((sum, value) => sum + value, 0) / scores.length
            : null;
        return {
          name: child.name,
          segments,
          total,
          average,
        };
      })
      .filter(Boolean)
      .filter((item) => item.total > 0);
  }, [
    selectedQuestion,
    selectedParentCategory,
    visibleCategories,
    get_employee_org_category,
    dataSource,
  ]);

  const selectedOpenEndedQuestion = useMemo(
    () =>
      openEndedQuestions.find(
        (question) => question.id === selectedOpenEndedId
      ) || null,
    [openEndedQuestions, selectedOpenEndedId]
  );

  const openEndedResponses = useMemo(
    () =>
      buildOpenEndedResponses(
        selectedOpenEndedQuestion,
        dataSource?.respondents,
        isAnonymous
      ),
    [selectedOpenEndedQuestion, dataSource, isAnonymous]
  );

  if (!pulseData) {
    return (
      <Container>
        <LoadingState>
          <LoadingSpinner />
          <div>Loading analytics…</div>
        </LoadingState>
      </Container>
    );
  }

  if (!dataSource?.questions?.length) {
    return (
      <Container>
        <EmptyState>
          <Icon name="chart bar outline" size="huge" />
          <h2>No Survey Data Available</h2>
          <p>
            There is currently no pulse survey data to display. Please check
            back once survey responses have been collected.
          </p>
        </EmptyState>
      </Container>
    );
  }

  const averageScore = selectedQuestion?.responses?.average;
  const totalResponses = selectedQuestion?.responses?.total ?? 0;

  return (
    <Container>
      <PrimaryTabs>
        <PrimaryTab
          type="button"
          onClick={() => setActivePrimaryTab("scores")}
          $active={activePrimaryTab === "scores"}
        >
          Score Distributions
        </PrimaryTab>
        <PrimaryTab
          type="button"
          onClick={() => setActivePrimaryTab("openEnded")}
          $active={activePrimaryTab === "openEnded"}
        >
          Open-Ended Responses
        </PrimaryTab>
      </PrimaryTabs>

      {activePrimaryTab === "scores" ? (
        <>
          {sharedRangeInfo?.ranges && (
            <LegendSection>
              <LegendBar>
                {DISTRIBUTION_SEGMENTS.map(({ key, label }) => {
                  const range = sharedRangeInfo.ranges?.[key];
                  if (!range) return null;
                  return (
                    <LegendItem key={`overall-${key}`}>
                      <LegendDot color={SEGMENT_META[key].color} />
                      <LegendValue>
                        {label} ({formatRange(range.start, range.end)})
                      </LegendValue>
                    </LegendItem>
                  );
                })}
              </LegendBar>
              {!sharedRangeInfo.consistent && (
                <LegendNote>
                  Ranges adjust slightly per question; see each row for exact
                  values.
                </LegendNote>
              )}
            </LegendSection>
          )}

          {selectedQuestion ? (
            <DetailPanel>
              <BackRow>
                <BackButton
                  type="button"
                  onClick={() => setSelectedQuestionId(null)}
                >
                  ← Back to all questions
                </BackButton>
              </BackRow>

              <DetailHeader>
                <DetailTitle>{selectedQuestion.text}</DetailTitle>
                <DetailSubtitle>{selectedQuestion.category}</DetailSubtitle>
                <DetailMeta>
                  <span>
                    Avg{" "}
                    {typeof averageScore === "number"
                      ? averageScore.toFixed(2)
                      : "—"}
                  </span>
                  <span>{totalResponses} responses</span>
                </DetailMeta>
              </DetailHeader>

              {parentCategoryOptions.length > 0 ? (
                <AnchorControls>
                  <span style={{ color: "#666D79", fontSize: 13 }}>
                    Category
                  </span>
                  <Select
                    value={selectedParentCategory || ""}
                    onChange={(event) =>
                      setSelectedParentCategory(event.target.value)
                    }
                  >
                    {parentCategoryOptions.map((option) => (
                      <option key={option.key} value={option.key}>
                        {option.name}
                      </option>
                    ))}
                  </Select>
                </AnchorControls>
              ) : null}

              {categoryDistributions.length ? (
                <CategoryTable>
                  <CategoryTableHeader>
                    <CategoryHeaderCell>Category</CategoryHeaderCell>
                    <CategoryHeaderCell>
                      Response Distribution
                    </CategoryHeaderCell>
                  </CategoryTableHeader>
                  <CategoryTableBody>
                    {categoryDistributions.map((category) => (
                      <CategoryTableRow key={category.name}>
                        <CategoryCell>
                          <CategoryName>{category.name}</CategoryName>
                          <CategoryMeta>
                            <span>
                              Avg{" "}
                              {typeof category.average === "number"
                                ? category.average.toFixed(2)
                                : "—"}
                            </span>
                            <span>{category.total} responses</span>
                          </CategoryMeta>
                        </CategoryCell>
                        <CategoryChartCell>
                          <QuestionDistributionChart data={category.segments} />
                        </CategoryChartCell>
                      </CategoryTableRow>
                    ))}
                  </CategoryTableBody>
                </CategoryTable>
              ) : (
                <ComparisonPlaceholder>
                  {parentCategoryOptions.length
                    ? "Not enough responses to show category distributions."
                    : "Category breakdowns are not configured for this survey."}
                </ComparisonPlaceholder>
              )}
            </DetailPanel>
          ) : quantitativeSummaries.length ? (
            <ScoresTable>
              <TableHeader>
                <HeaderCell>Question</HeaderCell>
                <HeaderCell>Response Distribution</HeaderCell>
              </TableHeader>
              <TableBody>
                {quantitativeSummaries.map((summary) => {
                  const { question, bands, ranges } = summary;
                  const average = question.responses?.average;
                  const responseCount = question.responses?.total ?? 0;

                  return (
                    <TableRow
                      key={question.id}
                      type="button"
                      onClick={() => setSelectedQuestionId(question.id)}
                    >
                      <QuestionCell>
                        <QuestionTitle>{question.text}</QuestionTitle>
                        <QuestionMeta>
                          <span>
                            Avg{" "}
                            {typeof average === "number"
                              ? average.toFixed(2)
                              : "—"}
                          </span>
                          <span>{responseCount} responses</span>
                        </QuestionMeta>
                      </QuestionCell>
                      <DistributionCell>
                        <DistributionWrapper>
                          <QuestionDistributionChart data={bands} />
                        </DistributionWrapper>
                        <DistributionFooter>
                          <ViewDetail>View by category →</ViewDetail>
                        </DistributionFooter>
                      </DistributionCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </ScoresTable>
          ) : (
            <ComparisonPlaceholder>
              No quantitative questions are available for this survey.
            </ComparisonPlaceholder>
          )}
        </>
      ) : (
        <OpenEndedContainer>
          <OpenEndedHeader>
            <OpenEndedTitle>
              {selectedOpenEndedQuestion
                ? selectedOpenEndedQuestion.text
                : "Open-Ended Responses"}
            </OpenEndedTitle>
            {selectedOpenEndedQuestion && (
              <OpenEndedMeta>
                {openEndedResponses.length} responses
              </OpenEndedMeta>
            )}
          </OpenEndedHeader>

          {openEndedQuestions.length > 1 && (
            <OpenEndedSelectRow>
              <span>Switch question</span>
              <Select
                value={selectedOpenEndedId || ""}
                onChange={(event) => setSelectedOpenEndedId(event.target.value)}
              >
                {openEndedQuestions.map((question) => (
                  <option key={question.id} value={question.id}>
                    {question.text}
                  </option>
                ))}
              </Select>
            </OpenEndedSelectRow>
          )}

          {selectedOpenEndedQuestion ? (
            openEndedResponses.length ? (
              <OpenEndedList>
                {openEndedResponses.map((item, index) => (
                  <OpenEndedCard
                    key={`${selectedOpenEndedQuestion.id}-${index}`}
                  >
                    <OpenEndedName>{item.name}</OpenEndedName>
                    <OpenEndedText>{item.response}</OpenEndedText>
                    {item.followUp && (
                      <FollowUpSection>
                        <FollowUpLabel>AI Follow-up</FollowUpLabel>
                        <FollowUpQuestion>
                          {item.followUp.question}
                        </FollowUpQuestion>
                        <FollowUpResponse>
                          {item.followUp.response}
                        </FollowUpResponse>
                      </FollowUpSection>
                    )}
                  </OpenEndedCard>
                ))}
              </OpenEndedList>
            ) : (
              <ComparisonPlaceholder>
                No responses yet for this question.
              </ComparisonPlaceholder>
            )
          ) : (
            <ComparisonPlaceholder>
              No open-ended questions are available for this survey.
            </ComparisonPlaceholder>
          )}
        </OpenEndedContainer>
      )}
    </Container>
  );
};

const Container = styled.div`
  background: #ffffff;
  font-family: "Raleway", sans-serif;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 40px 48px;
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 40px;
  color: #666d79;
`;

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f0f2f5;
  border-top: 4px solid #2d70e2;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  margin-bottom: 16px;
`;

const PrimaryTabs = styled.div`
  display: flex;
  gap: 16px;
  margin: 24px 0 32px;
`;

const PrimaryTab = styled.button`
  background: none;
  border: none;
  border-bottom: 2px solid ${(props) => (props.$active ? "#2D70E2" : "transparent")};
  padding: 12px 0;
  font-size: 15px;
  font-weight: 600;
  color: ${(props) => (props.$active ? "#2D70E2" : "#666D79")};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: #2d70e2;
  }
`;

const ScoresTable = styled.div`
  border: 1px solid #e1e4e8;
  border-radius: 12px;
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1.8fr);
  background: #f8f9fc;
  padding: 16px 24px;
  font-size: 13px;
  font-weight: 600;
  color: #4d5562;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    row-gap: 8px;
  }
`;

const HeaderCell = styled.div``;

const TableBody = styled.div`
  display: flex;
  flex-direction: column;
`;

const TableRow = styled.button`
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1.8fr);
  gap: 24px;
  align-items: stretch;
  padding: 20px 24px;
  border: none;
  border-top: 1px solid #edf0f5;
  background: #ffffff;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s ease, box-shadow 0.2s ease;

  &:first-child {
    border-top: none;
  }

  &:hover {
    background: #f8faff;
    box-shadow: inset 0 0 0 1px #dbe4ff;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const QuestionCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const DistributionCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  justify-content: center;
`;

const DistributionWrapper = styled.div`
  width: 100%;
`;

const DistributionFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  font-size: 12px;
  font-weight: 600;
  color: #2d70e2;
  margin-top: 4px;

  @media (max-width: 900px) {
    justify-content: flex-start;
  }
`;

const ViewDetail = styled.span`
  white-space: nowrap;
`;

const CategoryTable = styled.div`
  border: 1px solid #e1e4e8;
  border-radius: 12px;
  overflow: hidden;
`;

const CategoryTableHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1.8fr);
  background: #f8f9fc;
  padding: 16px 24px;
  font-size: 13px;
  font-weight: 600;
  color: #4d5562;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    row-gap: 8px;
  }
`;

const CategoryHeaderCell = styled.div``;

const CategoryTableBody = styled.div`
  display: flex;
  flex-direction: column;
`;

const CategoryTableRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1.8fr);
  gap: 24px;
  align-items: center;
  padding: 20px 24px;
  border-top: 1px solid #edf0f5;

  &:first-child {
    border-top: none;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const CategoryCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const CategoryChartCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const QuestionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #2a3039;
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const QuestionMeta = styled.div`
  display: flex;
  gap: 12px;
  font-size: 13px;
  color: #666d79;
  flex-wrap: wrap;
`;

const DetailPanel = styled.div`
  background: #ffffff;
  border: 1px solid #e1e4e8;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const BackRow = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #2d70e2;
  font-size: 14px;
  font-weight: 600;
  padding: 4px 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    color: #1b4fa0;
  }
`;

const DetailHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DetailTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #2a3039;
  margin: 0;
`;

const DetailSubtitle = styled.p`
  font-size: 14px;
  color: #666d79;
  margin: 0;
`;

const DetailMeta = styled.div`
  display: flex;
  gap: 16px;
  font-size: 13px;
  color: #707784;
  flex-wrap: wrap;
`;

const AnchorControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const Select = styled.select`
  appearance: none;
  background: white;
  border: 1px solid #e1e4e8;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 13px;
  color: #2a3039;
`;

const CategoryName = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #2a3039;
  margin: 0;
`;

const CategoryMeta = styled.div`
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #707784;
  flex-wrap: wrap;
`;

const LegendSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
  align-items: flex-end;
`;

const LegendBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
  justify-content: flex-end;
  text-align: right;
`;

const LegendNote = styled.span`
  font-size: 12px;
  color: #707784;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const LegendDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(props) => props.color};
  display: inline-block;
`;

const LegendValue = styled.span`
  font-size: 12px;
  color: #666d79;
`;

const ComparisonPlaceholder = styled.div`
  background: #f5f7fa;
  border: 1px dashed #d7dce6;
  border-radius: 10px;
  padding: 32px;
  color: #707784;
  font-size: 14px;
  text-align: center;
`;

const OpenEndedContainer = styled.div`
  background: #ffffff;
  border: 1px solid #e1e4e8;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const OpenEndedHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OpenEndedTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #2a3039;
  margin: 0;
`;

const OpenEndedMeta = styled.span`
  font-size: 13px;
  color: #707784;
`;

const OpenEndedSelectRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  color: #666d79;
`;

const OpenEndedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const OpenEndedCard = styled.div`
  border: 1px solid #edf0f5;
  border-radius: 10px;
  padding: 18px;
  background: #ffffff;
  box-shadow: 0 4px 12px rgba(31, 52, 88, 0.06);
`;

const OpenEndedName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #2a3039;
  margin-bottom: 8px;
`;

const OpenEndedText = styled.div`
  font-size: 14px;
  color: #424752;
  line-height: 1.5;
  white-space: pre-line;
`;

const FollowUpSection = styled.div`
  margin-top: 16px;
  padding: 16px;
  background: #f8faff;
  border-left: 4px solid #2d70e2;
  border-radius: 0 10px 10px 0;
`;

const FollowUpLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #2d70e2;
  margin-bottom: 8px;
`;

const FollowUpQuestion = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #2a3039;
  margin-bottom: 6px;
  font-style: italic;
`;

const FollowUpResponse = styled.div`
  font-size: 14px;
  color: #424752;
  line-height: 1.5;
`;

const EmptyState = styled.div`
  padding: 96px 40px;
  text-align: center;
  color: #666d79;
  border: 1px dashed #d7dce6;
  border-radius: 12px;
  background: #ffffff;

  h2 {
    margin-top: 16px;
    font-size: 24px;
    color: #2a3039;
    font-weight: 600;
  }

  p {
    font-size: 16px;
    line-height: 1.5;
    max-width: 420px;
    margin: 12px auto 0;
  }
`;

export default PulseAnalytics;

